import { useState, useRef, useCallback } from "react";
import api from "../api";
import { COMPANY_OPTIONS } from "../constants/companies";

type TabId = "file" | "form" | "history";

interface UploadDialogProps {
  open: boolean;
  onClose: () => void;
}

export default function UploadDialog({ open, onClose }: UploadDialogProps) {
  const [tab, setTab] = useState<TabId>("file");
  const [company, setCompany] = useState("ant");
  const [title, setTitle] = useState("");
  const [tags, setTags] = useState("");
  const [body, setBody] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  interface UploadHistoryItem {
    id: number;
    title: string;
    company: string;
    content_type: string;
    created_at?: string;
    original_filename?: string;
  }

  const [history, setHistory] = useState<UploadHistoryItem[]>([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);

  const loadHistory = useCallback(async () => {
    try {
      const r = await api.get("/upload/list");
      setHistory(r.data.data.items || []);
      setHistoryLoaded(true);
    } catch { /* ignore */ }
  }, []);

  const handleFileUpload = async () => {
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("company", company);
      formData.append("title", title || file.name);
      if (tags) formData.append("tags", tags);

      const r = await api.post("/upload/file", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(`✅ 上传成功！已创建文章 #${r.data.data.article_id}，提取 ${r.data.data.text_length} 字`);
      setFile(null);
      setTitle("");
      setTags("");
      if (fileRef.current) fileRef.current.value = "";
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setResult(`❌ 上传失败: ${err.response?.data?.detail || err.message || "未知错误"}`);
    } finally {
      setUploading(false);
    }
  };

  const handleFormSubmit = async () => {
    if (!title.trim() || !body.trim()) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("company", company);
      formData.append("title", title.trim());
      formData.append("body", body.trim());
      if (tags) formData.append("tags", tags);

      const r = await api.post("/upload/form", formData);
      setResult(`✅ 提交成功！已创建文章 #${r.data.data.article_id}`);
      setTitle("");
      setBody("");
      setTags("");
    } catch (e: unknown) {
      const err = e as { response?: { data?: { detail?: string } }; message?: string };
      setResult(`❌ 提交失败: ${err.response?.data?.detail || err.message || "未知错误"}`);
    } finally {
      setUploading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-lg rounded-2xl border border-border bg-panel shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <h2 className="text-[16px] font-semibold text-ink">上传竞品信息</h2>
          <button onClick={onClose} className="text-ink-faint hover:text-ink-secondary transition-colors">
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border px-5">
          {([
            { id: "file", label: "文件上传" },
            { id: "form", label: "快速笔记" },
            { id: "history", label: "上传记录" },
          ] as { id: TabId; label: string }[]).map((t) => (
            <button
              key={t.id}
              onClick={() => {
                setTab(t.id);
                if (t.id === "history" && !historyLoaded) loadHistory();
              }}
              className={`-mb-px border-b-2 px-3 py-2.5 text-xs font-medium transition-colors ${
                tab === t.id
                  ? "border-accent text-accent-dark dark:text-accent"
                  : "border-transparent text-ink-tertiary hover:text-ink-secondary"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-3">
          {/* Company selector — shared across file & form tabs */}
          {tab !== "history" && (
            <div>
              <label className="text-label mb-1.5 block">关联公司</label>
              <div className="flex gap-1.5">
                {COMPANY_OPTIONS.map((c) => (
                  <button
                    key={c.value}
                    onClick={() => setCompany(c.value)}
                    className={`flex-1 rounded-lg border px-2 py-1.5 text-xs font-medium transition-colors ${
                      company === c.value
                        ? "border-primary-400 bg-primary-50 text-primary-600 dark:border-primary-600 dark:bg-primary-900/20 dark:text-primary-400"
                        : "border-border text-ink-tertiary hover:border-border-strong hover:text-ink-secondary"
                    }`}
                  >
                    {c.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* File tab */}
          {tab === "file" && (
            <>
              <div>
                <label className="text-label mb-1.5 block">选择文件</label>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.docx,.doc,.xlsx,.xls,.csv,.txt,.md"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="w-full rounded-lg border-subtle bg-surface px-3 py-2 text-sm text-ink file:mr-3 file:rounded file:border-0 file:bg-primary-50 file:px-3 file:py-1 file:text-xs file:font-medium file:text-primary-600"
                />
                <p className="mt-1 text-2xs text-ink-faint">支持 PDF、Word、Excel、CSV，最大 50MB</p>
              </div>
              <div>
                <label className="text-label mb-1.5 block">标题（可选，默认用文件名）</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="如：蚂蚁集团2025年经营数据分析"
                  className="w-full rounded-lg border-subtle bg-surface px-3 py-2 text-sm text-ink placeholder-ink-faint outline-none focus:border-border-strong"
                />
              </div>
              <div>
                <label className="text-label mb-1.5 block">标签（逗号分隔，可选）</label>
                <input
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="如：财报,战略,小微"
                  className="w-full rounded-lg border-subtle bg-surface px-3 py-2 text-sm text-ink placeholder-ink-faint outline-none focus:border-border-strong"
                />
              </div>
              <button
                onClick={handleFileUpload}
                disabled={!file || uploading}
                className="w-full rounded-lg bg-primary-700 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-800 disabled:opacity-50 active:scale-[0.98] dark:bg-primary-500 dark:hover:bg-primary-600"
              >
                {uploading ? "上传中..." : "上传文件"}
              </button>
            </>
          )}

          {/* Form tab */}
          {tab === "form" && (
            <>
              <div>
                <label className="text-label mb-1.5 block">标题</label>
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="如：京东白条2025Q1策略调整分析"
                  className="w-full rounded-lg border-subtle bg-surface px-3 py-2 text-sm text-ink placeholder-ink-faint outline-none focus:border-border-strong"
                />
              </div>
              <div>
                <label className="text-label mb-1.5 block">正文</label>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={6}
                  placeholder="输入竞品信息内容..."
                  className="w-full rounded-lg border-subtle bg-surface px-3 py-2 text-sm text-ink placeholder-ink-faint outline-none focus:border-border-strong resize-y"
                />
              </div>
              <div>
                <label className="text-label mb-1.5 block">标签（逗号分隔，可选）</label>
                <input
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="如：战略,ABS,利率"
                  className="w-full rounded-lg border-subtle bg-surface px-3 py-2 text-sm text-ink placeholder-ink-faint outline-none focus:border-border-strong"
                />
              </div>
              <button
                onClick={handleFormSubmit}
                disabled={!title.trim() || !body.trim() || uploading}
                className="w-full rounded-lg bg-primary-700 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-800 disabled:opacity-50 active:scale-[0.98] dark:bg-primary-500 dark:hover:bg-primary-600"
              >
                {uploading ? "提交中..." : "提交笔记"}
              </button>
            </>
          )}

          {/* History tab */}
          {tab === "history" && (
            <div className="max-h-[320px] overflow-y-auto">
              {history.length === 0 ? (
                <p className="py-8 text-center text-sm text-ink-tertiary">暂无上传记录</p>
              ) : (
                <div className="divide-y divide-border">
                  {history.map((h) => (
                    <div key={h.id} className="py-2.5 text-sm">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-ink">{h.title}</span>
                        <span className="rounded border border-border px-1.5 py-px text-2xs text-ink-tertiary">
                          {COMPANY_OPTIONS.find((c) => c.value === h.company)?.label || h.company}
                        </span>
                        <span className="rounded border border-border px-1.5 py-px text-2xs text-ink-faint">
                          {h.content_type}
                        </span>
                      </div>
                      <p className="mt-0.5 text-2xs text-ink-faint">
                        {h.created_at ? new Date(h.created_at).toLocaleString("zh-CN") : ""}
                        {h.original_filename && ` · ${h.original_filename}`}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Result message */}
          {result && (
            <div className={`rounded-lg px-3 py-2 text-xs ${
              result.startsWith("✅")
                ? "border border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300"
                : "border border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-800 dark:bg-rose-900/20 dark:text-rose-300"
            }`}>
              {result}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
